import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
  Link,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircleOutlined as CheckCircleIcon,
  RadioButtonUnchecked as UnassignedIcon,
} from "@mui/icons-material";
import Button from "@/components/ui/Button";
import { UIComponentSize, ButtonStyleType } from "@/types/shared/uiComponents";
import { FileAssignment, FileUploadStatus, Vendor } from "@/types";
import { formatFileSize } from "@/utils/formatters/fileUtils";
import { administratorRoleLabel, type VendorRoleMeta } from "./policyVendorSelector";
import {
  FileClientChoice,
  OTHER_VENDOR,
  VENDOR_TYPE_CONFIG,
  getFileTypeLabel,
  needsSheetSelection,
} from "./vendorAssignmentShared";
import { ClientSearchField } from "./ClientSearchField";
import { AdministratorChoices } from "./AdministratorChoices";
import { SheetPicker } from "./SheetPicker";

// =============================================================================
// File Row
// =============================================================================

export interface FileRowProps {
  assignment: FileAssignment;
  vendors: Vendor[];
  /** Groups the options into "This policy's vendors" / "All vendors" (#5591). */
  groupBy?: (option: Vendor) => string;
  /** Shared option renderer that appends the primary/run-out role tag. */
  renderVendorOption: (
    props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
    option: Vendor
  ) => React.ReactNode;
  /** The policy's administrators (radio choices). Empty → search only. */
  policyChoices: Vendor[];
  roleMeta: Map<string, VendorRoleMeta>;
  runoutCutover?: string;
  /** The row picked "Different vendor…" — the search is showing. */
  otherVendorMode: boolean;
  /** 3+ files layout: show the current pick with a Change link instead of the group. */
  compact: boolean;
  onExpand: () => void;
  config: (typeof VENDOR_TYPE_CONFIG)[keyof typeof VENDOR_TYPE_CONFIG];
  loadingVendors: boolean;
  /** The vendor list is still loading and will yield policy radios: show a placeholder, not the search. */
  choicesPending?: boolean;
  isUploading: boolean;
  onAssign: (fileId: string, vendor: Vendor | null) => void;
  onChoiceChange: (fileId: string, value: string) => void;
  onRemove: (fileId: string) => void;
  onSheetChange: (fileId: string, sheetName: string, checked: boolean) => void;
  onSelectAllSheets: (fileId: string, checked: boolean) => void;
  /** Whether the multi-client toggle applies (reimbursement uploads only). */
  multiClientEligible: boolean;
  onMultiClientChange: (fileId: string, checked: boolean) => void;
  /** The client this file binds to; undefined = the dialog has no client picker wired. */
  client: FileClientChoice | null | undefined;
  clientEditable: boolean;
  onClientChange: (client: FileClientChoice | null) => void;
}

export const FileRow = ({
  assignment,
  vendors,
  groupBy,
  renderVendorOption,
  policyChoices,
  roleMeta,
  runoutCutover,
  otherVendorMode,
  compact,
  onExpand,
  config,
  loadingVendors,
  choicesPending = false,
  isUploading,
  onAssign,
  onChoiceChange,
  onRemove,
  onSheetChange,
  onSelectAllSheets,
  multiClientEligible,
  onMultiClientChange,
  client,
  clientEditable,
  onClientChange,
}: FileRowProps) => {
  const isAssigned = assignment.status === FileUploadStatus.ASSIGNED;
  const showSheetPicker = needsSheetSelection(assignment);
  const [editingClient, setEditingClient] = useState(false);

  const hasChoices = policyChoices.length > 0;
  const assignedIsPolicyVendor = !!assignment.carrierId && policyChoices.some((v) => v.id === assignment.carrierId);
  // Radio value: a policy vendor, "Different vendor…" (chosen, or a non-policy vendor is set), or none.
  const choiceValue = assignedIsPolicyVendor
    ? String(assignment.carrierId)
    : otherVendorMode || assignment.carrierId
      ? OTHER_VENDOR
      : "";
  const showSearch = !hasChoices || choiceValue === OTHER_VENDOR;
  const assignedMeta = assignment.carrierId ? roleMeta.get(String(assignment.carrierId)) : undefined;

  const searchField = (
    <Autocomplete
      size="small"
      sx={{ flex: 1, minWidth: 180, maxWidth: 420 }}
      options={vendors}
      groupBy={groupBy}
      getOptionLabel={(option) => option.company_name || ""}
      value={vendors.find((v) => v.id === assignment.carrierId) || null}
      onChange={(_e, newValue) => onAssign(assignment.id, newValue)}
      disabled={loadingVendors || isUploading}
      autoFocus={hasChoices}
      openOnFocus={hasChoices}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={`Select ${config.label}...`}
          variant="outlined"
          inputProps={{ ...params.inputProps, "aria-label": `${config.label} for ${assignment.filename}` }}
        />
      )}
      renderOption={renderVendorOption}
    />
  );

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        px: 2,
        py: 1.5,
        transition: "background-color 150ms",
        "&:hover": {
          bgcolor: alpha(theme.palette.action.hover, 0.04),
        },
      })}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {/* Status icon */}
        {isAssigned ? (
          <CheckCircleIcon sx={{ fontSize: 20, color: "success.main", flexShrink: 0 }} />
        ) : (
          <UnassignedIcon sx={{ fontSize: 20, color: "text.disabled", flexShrink: 0 }} />
        )}

        {/* Filename + size + client */}
        <Tooltip title={assignment.filename} enterDelay={500}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {assignment.filename}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {formatFileSize(assignment.size)}
              {client !== undefined && (
                <>
                  {" · Client "}
                  <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
                    {assignment.is_multi_client ? "All clients in file" : (client?.name ?? "Chosen at review")}
                  </Box>
                  {clientEditable && !assignment.is_multi_client && !editingClient && (
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => setEditingClient(true)}
                      disabled={isUploading}
                      sx={{ ml: 1, fontSize: "inherit", fontWeight: 600, verticalAlign: "baseline" }}
                    >
                      Change
                    </Link>
                  )}
                </>
              )}
            </Typography>
          </Box>
        </Tooltip>

        {/* File type chip */}
        <Chip
          label={getFileTypeLabel(assignment.type)}
          size="small"
          variant="filled"
          sx={{ flexShrink: 0, fontWeight: 500, fontSize: "0.7rem" }}
        />

        {/* 3+ files: current pick + Change (#7552 AC6). */}
        {hasChoices && compact && (
          <Typography variant="body2" sx={{ flex: 1, minWidth: 180 }}>
            {assignment.carrierName ?? (
              <Box component="span" sx={{ color: "text.disabled" }}>
                Unassigned
              </Box>
            )}
            {assignedMeta && (
              <Box component="span" sx={{ color: "text.secondary" }}>
                {" · "}
                {administratorRoleLabel(assignedMeta)}
              </Box>
            )}
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={onExpand}
              disabled={isUploading}
              sx={{ ml: 1, fontWeight: 600 }}
            >
              Change
            </Link>
          </Typography>
        )}

        {/* Remove button */}
        <Tooltip title="Remove file">
          <IconButton
            size="small"
            onClick={() => onRemove(assignment.id)}
            disabled={isUploading}
            sx={{ flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {editingClient && (
        <Box sx={{ pl: 4.5, pt: 1, display: "flex", gap: 1, alignItems: "center" }}>
          <ClientSearchField
            value={client ?? null}
            autoFocus
            ariaLabel={`Client for ${assignment.filename}`}
            onChange={(c) => {
              onClientChange(c);
              setEditingClient(false);
            }}
            disabled={isUploading}
          />
          <Button variant={ButtonStyleType.Text} size={UIComponentSize.Small} onClick={() => setEditingClient(false)}>
            Done
          </Button>
        </Box>
      )}

      {/* Radios on the way: a quiet placeholder keeps the row from flickering
          search → radios when the vendor list lands. */}
      {!hasChoices && choicesPending && (
        <Box sx={{ pl: 4.5, pt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Loading administrators…
          </Typography>
        </Box>
      )}

      {/* No policy choices → the search sits under the row, where the radios would be. */}
      {!hasChoices && !choicesPending && <Box sx={{ pl: 4.5, pt: 1.5, display: "flex" }}>{searchField}</Box>}

      {hasChoices && !compact && (
        <Box sx={{ pl: 4.5, pt: 1.5 }}>
          <AdministratorChoices
            name={`administrator-${assignment.id}`}
            ariaLabel={`Administrator for ${assignment.filename}`}
            title="Administrator for this file"
            choices={policyChoices}
            roleMeta={roleMeta}
            runoutCutover={runoutCutover}
            value={choiceValue}
            disabled={loadingVendors || isUploading}
            onChange={(v) => onChoiceChange(assignment.id, v)}
            withOther
            otherHelper={`Search all ${config.labelPlural}.`}
          />
          {showSearch && <Box sx={{ mt: 1, display: "flex" }}>{searchField}</Box>}
        </Box>
      )}

      {showSheetPicker && (
        <SheetPicker
          sheetNames={assignment.sheetNames ?? []}
          selectedSheets={assignment.selectedSheets ?? []}
          filename={assignment.filename}
          disabled={isUploading}
          onSheetChange={(sheetName, checked) => onSheetChange(assignment.id, sheetName, checked)}
          onSelectAll={(checked) => onSelectAllSheets(assignment.id, checked)}
        />
      )}

      {/* Multi-client ("book of business") toggle — reimbursement uploads only.
          When set, the file uploads with no single client (X-Multi-Client) and
          each row resolves its own client/policy on review/load (#5309 / ADR-057). */}
      {multiClientEligible && (
        <FormControlLabel
          sx={{ ml: 4.5, mt: 0.5 }}
          control={
            <Checkbox
              size="small"
              checked={!!assignment.is_multi_client}
              onChange={(_e, checked) => onMultiClientChange(assignment.id, checked)}
              disabled={isUploading}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              This file covers multiple clients (book of business) — rows resolve a client/policy each
            </Typography>
          }
        />
      )}
    </Box>
  );
};
